import { For, Show, useContext, createMemo, onCleanup, createSignal, Switch, Match, createEffect } from 'solid-js';
import { AppointmentInfo, DateStatus } from 'st-app/api';
import { uuidv4, date } from 'common';
import { Appointment, FullAppointment } from './Appointment';
import { requireCalendarContext } from '../st-schedule-grid';
import styles from '../st-schedule-grid.module.css';
import Dorito from '../../st-dorito/st-dorito';
import Icon from '../../st-icon/st-icon';
import Button from '../../st-button/st-button';

type CellSize = 'small' | 'medium' | 'large' | 'full';
type DisplayType = 'bullet' | 'icon' | 'details';

export function Day(props: { date: Date; }) {
    const ctx = requireCalendarContext();

    return (
        <Switch>
            <Match when={ctx.displayMode() === 'Month'}>
                <MonthlyDay {...props} />
            </Match>
            <Match when={ctx.displayMode() === 'Week' || ctx.displayMode() === 'WorkWeek'}>
                <WeeklyDay {...props} />
            </Match>
            <Match when={ctx.displayMode() === 'Day'}>
                <DailyDay {...props} />
            </Match>
        </Switch>
    );
}


function MonthlyDay(props: { date: Date; }) {
    const ctx = requireCalendarContext();
    const MIN_WIDTH = 150;
    const [showAppointmentDetails, setShowAppointmentDetails] = createSignal(true);
    const [cellSize, setCellSize] = createSignal<CellSize>('large');

    const isToday = createMemo(() => date.truncateTime(props.date).getTime() === date.today().getTime());

    const showMonthName = createMemo(() => {
        const day = ctx.day();

        // this is the first day of the month
        if (props.date.getTime() === date.startOfMonth(day).getTime())
            return true;

        // this is the first day of the calendar
        if (props.date.getMonth() !== day.getMonth()
            && props.date.getTime() === date.startOfWeek(day).getTime()) {
            return true;
        }

        // this is the first day of next month
        if (props.date.getMonth() !== day.getMonth() && props.date.getDate() === 1) {
            return true;
        }

        return false;
    });

    const cellDisplayType = createMemo<DisplayType>(() => {
        switch (cellSize()) {
            case 'small':
                return 'bullet';
            case 'medium':
                return 'icon';
            case 'large':
            case 'full':
            default:
                return 'details';
        }
    });

    function init(el: HTMLDivElement) {
        const resizeObserver = new ResizeObserver(entries => {
            setShowAppointmentDetails(true);

            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                const fontSize = Math.min(1, width / MIN_WIDTH);

                el.style.fontSize = `${Math.max(0.65, fontSize)}em`;

                setShowAppointmentDetails(fontSize > 0.65)


                switch (ctx.displayMode()) {
                    case 'Day':
                        setCellSize('full');
                        break;
                    case 'Week':
                        setCellSize('full');
                        break;
                    case 'Month':
                        if (width < 60) {
                            setCellSize('small');
                        }
                        else if (width < 100) {
                            setCellSize('medium');
                        }
                        else {
                            setCellSize('large');
                        }
                        break;
                }
            }
        });
        resizeObserver.observe(el);
        onCleanup(() => resizeObserver.disconnect());
    }

    const isEnabled = createMemo(() => ctx.dayEnabled(props.date));
    const disabledClass = createMemo(() => {
        const res: string[] = [];

        if (!isEnabled()) {
            res.push(styles.disabled);
        }

        const startOfMonth = date.startOfMonth(ctx.day());
        const endOfMonth = date.endOfMonth(ctx.day());

        if (startOfMonth.getTime() > props.date.getTime()
            || endOfMonth.getTime() < props.date.getTime()) {
            res.push(styles['other-month']);
        }

        return res.join(' ');
    });
    const activeClass = createMemo(() => date.today().getTime() === date.truncateTime(props.date).getTime() ? styles.active : '');
    const appointments = createMemo(() => ctx.getAppointments(props.date));

    return <div class={`cell ${disabledClass()} ${activeClass()} ${styles.cell}`}
        ref={init}
        style={{
            'min-height': (ctx.minCellHeight[0]()),
            'z-index': (7 - props.date.getDay()).toString()
        }}>

        <Switch>
            <Match when={cellSize() === 'small'}>
                <Show when={isToday()}>
                    <Dorito type="info" tooltip="Today" />
                </Show>
                <div class="flexbox vertical middle" style="font-size: 24px">
                    <div class="auto"></div>
                    <div>{props.date.getDate()}</div>
                    <Switch fallback={<div>&nbsp;</div>}>
                        <Match when={appointments().length > 0}>
                            <div>&#8226;</div>
                        </Match>
                        <Match when={ctx.afterAddClicked}>
                            <AddButton {...props} />
                        </Match>
                    </Switch>
                    <div class="auto"></div>
                </div>
            </Match>
            <Match when={cellSize() === 'medium'}>
                <Show when={isToday()}>
                    <Dorito type="info" tooltip="Today" />
                </Show>
                <Show when={showMonthName()}>
                    {date.shortMonthName(props.date)}&nbsp;
                </Show>
                {props.date.getDate()}

                <Show when={isEnabled()}>
                    <div style={{ position: 'relative', 'background-color': 'inherit' }}>
                        <Show when={appointments().length == 0}>
                            <AddButton {...props} />
                        </Show>

                        <For each={appointments()}>{appt => {
                            const apptId = appt.ID || uuidv4();

                            return <Appointment date={props.date}
                                {...appt}
                                displayType={cellDisplayType()}
                                id={apptId}
                                title={appt.title || 'Appointment'}
                            />
                        }}</For>
                    </div>
                </Show>
            </Match>
            <Match when={cellSize() === 'large'}>
                <Show when={isToday()}>
                    <Dorito type="info" tooltip="Today" />
                </Show>
                <Show when={showMonthName()}>
                    {date.shortMonthName(props.date)}&nbsp;
                </Show>
                {props.date.getDate()}

                <Show when={isEnabled()}>
                    <div style={{ position: 'relative', 'background-color': 'inherit' }}>
                        <Show when={appointments().length == 0}>
                            <AddButton {...props} />
                        </Show>

                        <For each={appointments()}>{appt => {
                            const apptId = appt.ID || uuidv4();

                            return <Appointment date={props.date}
                                {...appt}
                                displayType={cellDisplayType()}
                                id={apptId}
                                title={appt.title || 'Appointment'}
                            />
                        }}</For>
                    </div>
                </Show>
            </Match>
            <Match when={cellSize() === 'full'}>
                <Show when={isToday()}>
                    <Dorito type="info" tooltip="Today" />
                </Show>
                <Show when={showMonthName()}>
                    {date.shortMonthName(props.date)}&nbsp;
                </Show>
                {props.date.getDate()}

                <Show when={isEnabled()}>
                    <div style={{ position: 'relative', 'background-color': 'inherit' }}>
                        <Show when={appointments().length == 0}>
                            <AddButton {...props} />
                        </Show>

                        <For each={appointments()}>{appt => {
                            const apptId = appt.ID || uuidv4();

                            return <Appointment date={props.date}
                                {...appt}
                                displayType={cellDisplayType()}
                                id={apptId}
                                title={appt.title || 'Appointment'}
                            />
                        }}</For>
                    </div>
                </Show>
            </Match>
        </Switch>
    </div>
}

function WeeklyDay(props: { date: Date; }) {
    const ctx = requireCalendarContext();
    const appointments = createMemo(() => ctx.getAppointments(props.date));

    return <div class={`flexbox vertical ${styles['week-day-container']}`}>
        <div class={styles['week-day']} >
            {props.date.getDate()}
        </div>
        <Show when={appointments().length == 0}>
            <AddButton {...props} />
        </Show>
        <For each={appointments()}>{appt =>
            <FullAppointment {...appt} calDate={props.date} />
        }</For>
    </div>
}

function DailyDay(props: { date: Date }) {
    const ctx = requireCalendarContext();
    const appointments = createMemo(() => ctx.getAppointments(props.date));

    const isToday = createMemo(() => date.truncateTime(props.date).getTime() === date.today().getTime());

    return <div class="flexbox vertical">
        <Show when={isToday()}>
            <Dorito type="info" tooltip="Today" />
        </Show>
        <Show when={appointments().length == 0}>
            <AddButton {...props} />
        </Show>
        <For each={appointments()}>{appt =>
            <FullAppointment {...appt} calDate={props.date} />
        }</For>
    </div>
}

function AddButton(props: { date: Date }) {
    const ctx = requireCalendarContext();

    const otherMonth = createMemo(() => {
        const startOfMonth = date.startOfMonth(ctx.day());
        const endOfMonth = date.endOfMonth(ctx.day());

        if (startOfMonth.getTime() > props.date.getTime()
            || endOfMonth.getTime() < props.date.getTime()) {
            return true;
        }
        return false;
    });

    const showButton = createMemo(() => {
        const other = otherMonth();
        const enabled = ctx.dayEnabled(props.date);

        return ctx.afterAddClicked && !other && enabled;
    });

    return <Show when={showButton()}>
        <div class={`flexbox vertical ${styles['btn-add']}`}>
            <Button type="link" icon="fa fa-add" tooltip="Add Entry" onClick={() => ctx.addClicked(props.date)} />
        </div>
    </Show>
}
